import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import {
  Leaf,
  User,
  Mail,
  Phone,
  MapPin,
  Loader2,
  CheckCircle,
  Lock,
  BookOpen,
  Bell,
  Heart,
  Sparkles,
  Shield,
  Gift,
  ArrowRight,
  Star,
  Brain,
  Wind,
  Apple,
} from "lucide-react";
import { toast } from "sonner";

const API_URL = window.location.origin;

// Immagini per la landing page
const HERO_IMAGES = [
  "https://customer-assets.emergentagent.com/job_3aa397d2-df3c-4e29-af8c-edf4212ba1cc/artifacts/h9da1x20_image.png",
  "https://customer-assets.emergentagent.com/job_3aa397d2-df3c-4e29-af8c-edf4212ba1cc/artifacts/yanew5ry_image.png",
];

const BENEFITS = [
  {
    icon: BookOpen,
    title: "Articoli Scientifici",
    description: "Accesso illimitato a contenuti verificati su meditazione, respirazione, alimentazione e benessere mentale.",
    color: "bg-teal-100 text-teal-600"
  },
  {
    icon: Bell,
    title: "Newsletter Personalizzata",
    description: "Ricevi settimanalmente articoli selezionati in base ai tuoi interessi. Niente spam, solo contenuti di valore.",
    color: "bg-purple-100 text-purple-600"
  },
  {
    icon: Brain,
    title: "AI Assistente",
    description: "Fai domande sulla tua salute e benessere al nostro assistente intelligente, disponibile 24/7.",
    color: "bg-indigo-100 text-indigo-600"
  },
  {
    icon: Gift,
    title: "100% Gratuito",
    description: "Nessun costo nascosto, nessun vincolo, nessun abbonamento. Accedi liberamente quando vuoi.",
    color: "bg-amber-100 text-amber-600"
  }
];

const TOPICS = [
  { icon: Heart, label: "Meditazione", color: "text-rose-500" },
  { icon: Wind, label: "Respirazione", color: "text-cyan-500" },
  { icon: Apple, label: "Alimentazione", color: "text-green-500" },
  { icon: Brain, label: "Mindfulness", color: "text-purple-500" },
  { icon: Sparkles, label: "Crescita Personale", color: "text-amber-500" },
];

export default function BenessereClubPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [gdprConsent, setGdprConsent] = useState(false);
  
  const [formData, setFormData] = useState({
    nome: "",
    cognome: "",
    email: "",
    password: "",
    confermaPassword: "",
    cellulare: "",
    citta: "",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome || !formData.cognome || !formData.email || !formData.password) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    
    if (formData.password !== formData.confermaPassword) {
      toast.error("Le password non coincidono");
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error("La password deve essere di almeno 6 caratteri");
      return;
    }
    
    if (!gdprConsent) {
      toast.error("Devi accettare l'informativa sulla privacy");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/public/register-lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          nome: formData.nome,
          cognome: formData.cognome,
          email: formData.email,
          password: formData.password,
          cellulare: formData.cellulare,
          citta: formData.citta,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        toast.success("Registrazione completata!");
      } else {
        toast.error(data.detail || "Errore nella registrazione");
      }
    } catch (error) {
      console.error("Registration error:", error);
      toast.error("Errore di connessione");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-teal-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center shadow-xl">
          <CardContent className="pt-10 pb-8">
            <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Benvenuto nel Club!</h2>
            <p className="text-slate-600 mb-4">
              La tua registrazione è completata. Controlla la tua email per confermare l'indirizzo.
            </p>
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-teal-800">
                Dopo la conferma potrai accedere a tutti i contenuti esclusivi e iniziare il tuo percorso di benessere.
              </p>
            </div>
            <Link to="/benessere">
              <Button className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700">
                Esplora i Contenuti
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50 via-white to-cyan-50" data-testid="benessere-club-page">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
                <Leaf className="w-5 h-5 text-white" />
              </div>
              <span className="font-serif text-lg font-semibold text-slate-800">La Scienza del Benessere</span>
            </Link>
            <Link to="/login">
              <Button variant="ghost" size="sm">
                Accedi
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-600/10 to-cyan-600/10" />
        <div className="max-w-7xl mx-auto px-4 py-16 sm:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Text */}
            <div className="text-center lg:text-left">
              <Badge className="bg-teal-100 text-teal-700 mb-4 px-4 py-1">
                <Gift className="w-3 h-3 mr-1" />
                Accesso Gratuito
              </Badge>
              <h1 className="font-serif text-4xl sm:text-5xl font-bold text-slate-900 mb-6 leading-tight">
                Il Tuo Percorso di{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-cyan-600">
                  Benessere
                </span>{" "}
                Inizia Qui
              </h1>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Unisciti alla nostra community e accedi <strong>gratuitamente</strong> a centinaia di articoli scientifici 
                su meditazione, respirazione, alimentazione e crescita personale. 
                Ricevi ogni settimana contenuti selezionati per te.
              </p>
              
              {/* Trust Badges */}
              <div className="flex flex-wrap gap-4 justify-center lg:justify-start mb-8">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Shield className="w-4 h-4 text-green-500" />
                  <span>Nessun Vincolo</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Lock className="w-4 h-4 text-green-500" />
                  <span>Privacy Garantita</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Star className="w-4 h-4 text-amber-500" />
                  <span>Contenuti Verificati</span>
                </div>
              </div>

              {/* Topics */}
              <div className="flex flex-wrap gap-2 justify-center lg:justify-start">
                {TOPICS.map((topic, i) => (
                  <Badge key={i} variant="outline" className="bg-white/80">
                    <topic.icon className={`w-3 h-3 mr-1 ${topic.color}`} />
                    {topic.label}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Right - Image */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={HERO_IMAGES[0]}
                  alt="Benessere e meditazione"
                  className="w-full h-80 sm:h-96 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
              </div>
              {/* Floating Card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4 hidden sm:block">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center">
                    <BookOpen className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-800">500+</p>
                    <p className="text-sm text-slate-500">Articoli Disponibili</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl font-bold text-slate-900 mb-4">
              Cosa Ottieni Iscrivendoti
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Tutto completamente gratuito, senza obblighi e senza carta di credito.
              L'applicazione è in continuo sviluppo con nuove funzionalità ogni settimana.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {BENEFITS.map((benefit, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6 text-center">
                  <div className={`w-14 h-14 mx-auto rounded-xl ${benefit.color} flex items-center justify-center mb-4`}>
                    <benefit.icon className="w-7 h-7" />
                  </div>
                  <h3 className="font-semibold text-slate-800 mb-2">{benefit.title}</h3>
                  <p className="text-sm text-slate-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Registration Form Section */}
      <section className="py-16 bg-gradient-to-b from-white to-teal-50" id="registrati">
        <div className="max-w-xl mx-auto px-4">
          <Card className="shadow-2xl border-0 overflow-hidden">
            <div className="bg-gradient-to-r from-teal-500 to-cyan-600 p-6 text-white text-center">
              <Leaf className="w-12 h-12 mx-auto mb-3 opacity-90" />
              <h2 className="text-2xl font-bold mb-1">Iscriviti Gratuitamente</h2>
              <p className="text-teal-100 text-sm">Inizia il tuo percorso di benessere oggi stesso</p>
            </div>
            
            <CardContent className="p-6 sm:p-8">
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Nome e Cognome */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="nome">Nome *</Label>
                    <div className="relative mt-1">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="nome"
                        name="nome"
                        value={formData.nome}
                        onChange={handleChange}
                        placeholder="Mario"
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="cognome">Cognome *</Label>
                    <Input
                      id="cognome"
                      name="cognome"
                      value={formData.cognome}
                      onChange={handleChange}
                      placeholder="Rossi"
                      className="mt-1"
                      required
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="mario.rossi@email.com"
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                {/* Password */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="password">Password *</Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="password"
                        name="password"
                        type="password"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="Min. 6 caratteri"
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="confermaPassword">Conferma *</Label>
                    <Input
                      id="confermaPassword"
                      name="confermaPassword"
                      type="password"
                      value={formData.confermaPassword}
                      onChange={handleChange}
                      placeholder="Ripeti password"
                      className="mt-1"
                      required
                    />
                  </div>
                </div>

                {/* Telefono e Città (opzionali) */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="cellulare">Telefono</Label>
                    <div className="relative mt-1">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="cellulare"
                        name="cellulare"
                        type="tel"
                        value={formData.cellulare}
                        onChange={handleChange}
                        placeholder="Opzionale"
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="citta">Città</Label>
                    <div className="relative mt-1">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="citta"
                        name="citta"
                        value={formData.citta}
                        onChange={handleChange}
                        placeholder="Opzionale"
                        className="pl-10"
                      />
                    </div>
                  </div>
                </div>

                {/* GDPR */}
                <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                  <Checkbox
                    id="gdpr"
                    checked={gdprConsent}
                    onCheckedChange={setGdprConsent}
                    className="mt-0.5"
                  />
                  <Label htmlFor="gdpr" className="text-sm text-slate-600 leading-relaxed cursor-pointer">
                    Accetto l'<Link to="/privacy" className="text-teal-600 hover:underline">informativa sulla privacy</Link> e 
                    acconsento a ricevere la newsletter con contenuti sul benessere. Posso cancellarmi in qualsiasi momento.
                  </Label>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white font-semibold rounded-xl shadow-lg shadow-teal-500/25"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Registrazione in corso...
                    </>
                  ) : (
                    <>
                      Iscriviti Gratis
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>

                <p className="text-xs text-center text-slate-500">
                  Hai già un account?{" "}
                  <Link to="/login" className="text-teal-600 hover:underline">
                    Accedi qui
                  </Link>
                </p>
              </form>
            </CardContent>
          </Card>

          {/* Bottom Note */}
          <div className="text-center mt-8 text-sm text-slate-500">
            <p className="flex items-center justify-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-500" />
              Piattaforma in continuo sviluppo - Nuove funzionalità ogni settimana
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
              <Leaf className="w-4 h-4 text-white" />
            </div>
            <span className="font-serif font-semibold">La Scienza del Benessere</span>
          </div>
          <p className="text-slate-400 text-sm mb-4">
            Contenuti scientifici per il tuo benessere quotidiano
          </p>
          <p className="text-slate-500 text-xs">
            © 2025 ODR Academy. Tutti i diritti riservati.
          </p>
        </div>
      </footer>
    </div>
  );
}
